import { Form, FormQuestion, FormSubmission } from '../types'
import { slugify } from '../utils/format'
import { supabase } from './supabaseClient'
import { formConfig, formRow, questionRow, toForm, toQuestion } from './mappers'
import { getDb, refresh, requireOrganizationId, saveConfig } from './store'
import { newId } from './authService'
import { logFailure, logSuccess } from '../utils/diagnostics'
import { AVA_FORM } from '../data/avaForm'

/**
 * Completa la configuración de un formulario para un visitante ANÓNIMO.
 *
 * La configuración que no tiene columna propia en `forms` (bloque de contacto,
 * título del bloque, aviso de privacidad, texto del botón…) vive en
 * `organization_settings`. Esa tabla está protegida por RLS para los miembros
 * de la organización, así que un visitante sin sesión puede recibir `null`.
 *
 * Antes eso hacía que la página pública mostrara el formulario SIN el paso de
 * contacto: en escritorio (con sesión) salían 7 pasos y en el móvil, abierto
 * desde WhatsApp sin sesión, solo 6 empezando por «¿Dónde se encuentra tu
 * negocio?». Aquí se detecta ese caso y se reponen los valores del bloque de
 * contacto para que la experiencia sea idéntica en todos los dispositivos.
 */
function publicFormConfig(
  config: Record<string, unknown> | null | undefined,
  context: Record<string, unknown>,
): Record<string, unknown> {
  const value = (config ?? {}) as Record<string, unknown>
  const contactFields = value.contactFields as unknown[] | undefined
  if (contactFields && contactFields.length > 0) return value

  logFailure(
    'publicFormConfig',
    {
      message:
        'La configuración pública del formulario no incluye el bloque de contacto (organization_settings no es legible sin sesión). Se aplican los valores por defecto para que el formulario empiece siempre por los datos de contacto.',
    },
    context,
  )

  return {
    ...value,
    contactFields: AVA_FORM.contactFields,
    contactPosition: value.contactPosition ?? 'inicio',
    contactTitle: value.contactTitle ?? AVA_FORM.contactTitle,
    submitLabel: value.submitLabel ?? AVA_FORM.submitLabel,
    privacyNotice: value.privacyNotice ?? AVA_FORM.privacyNotice,
  }
}

export function listForms(organizationId: string): Form[] {
  return getDb().forms.filter((f) => f.organizationId === organizationId)
}

export function getForm(formId: string): Form | undefined {
  return getDb().forms.find((f) => f.id === formId)
}

export function getFormBySlug(slug: string): Form | undefined {
  return getDb().forms.find((f) => f.slug === slug)
}

export function listSubmissions(formId: string): FormSubmission[] {
  return getDb().submissions.filter((s) => s.formId === formId)
}

export function countSubmissions(organizationId: string, formId: string): number {
  return getDb().submissions.filter(
    (s) => s.organizationId === organizationId && s.formId === formId,
  ).length
}

export function emptyQuestion(formId: string, order: number): FormQuestion {
  return {
    id: newId('q'),
    formId,
    label: 'Nueva pregunta',
    type: 'texto_corto',
    options: [],
    required: false,
    order,
    isActive: true,
    conditionalLogic: {
      enabled: false,
      questionId: null,
      operator: 'igual',
      value: '',
    },
  }
}

/** Descarga un formulario público activo (visitante anónimo). */
export async function fetchPublicForm(slug: string): Promise<Form | null> {
  const { data, error } = await supabase
    .from('forms')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .is('deleted_at', null)
    .maybeSingle()
  // Un fallo de red se propaga (la vista reintenta); «no existe» devuelve null.
  if (error) {
    logFailure('fetchPublicForm', error, { slug })
    throw new Error(error.message)
  }
  if (!data) return null

  const [{ data: questions }, { data: settings, error: settingsError }] =
    await Promise.all([
      supabase.from('form_questions').select('*').eq('form_id', data.id).order('position'),
      supabase
        .from('organization_settings')
        .select('value')
        .eq('organization_id', data.organization_id)
        .eq('key', `form_config:${data.id}`)
        .maybeSingle(),
    ])
  if (settingsError) {
    logFailure('fetchPublicForm.config', settingsError, { slug, formId: data.id })
  }

  const config = publicFormConfig(settings?.value, { slug, formId: data.id })
  return toForm(data, config, (questions ?? []).map(toQuestion))
}

/** Descarga por id el formulario asociado a una landing pública. */
export async function fetchPublicFormById(formId: string): Promise<Form | null> {
  const { data, error } = await supabase
    .from('forms')
    .select('*')
    .eq('id', formId)
    .is('deleted_at', null)
    .maybeSingle()

  if (error) {
    logFailure('fetchPublicFormById', error, { formId })
    return null
  }
  if (!data) {
    logFailure(
      'fetchPublicFormById',
      {
        message: `La landing apunta a un formulario (${formId}) que no es legible públicamente. Debe estar activo y sin eliminar.`,
      },
      { formId },
    )
    return null
  }

  const [{ data: questions }, { data: settings, error: settingsError }] =
    await Promise.all([
      supabase
        .from('form_questions')
        .select('*')
        .eq('form_id', data.id)
        .order('position'),
      supabase
        .from('organization_settings')
        .select('value')
        .eq('organization_id', data.organization_id)
        .eq('key', `form_config:${data.id}`)
        .maybeSingle(),
    ])
  if (settingsError) {
    logFailure('fetchPublicFormById.config', settingsError, { formId })
  }

  const mapped = (questions ?? []).map(toQuestion)
  const config = publicFormConfig(settings?.value, { formId })
  logSuccess('fetchPublicFormById', {
    formId,
    nombre: data.name,
    activo: data.is_active,
    preguntas: mapped.length,
    preguntasActivas: mapped.filter((q) => q.isActive).length,
    bloqueContacto: (config.contactFields as unknown[])?.length ?? 0,
  })

  return toForm(data, config, mapped)
}

export async function createForm(
  organizationId: string,
  name: string,
): Promise<Form> {
  requireOrganizationId(organizationId, 'crear el formulario')
  const form: Form = {
    id: newId('form'),
    organizationId,
    name,
    description: '',
    slug: slugify(name) || `formulario-${Date.now()}`,
    status: 'borrador',
    submitLabel: 'ENVIAR',
    successMessage: 'Hemos recibido tu solicitud.',
    contactFields: [
      { key: 'firstName', label: 'Nombre', type: 'texto_corto', required: true },
      { key: 'lastName', label: 'Apellidos', type: 'texto_corto', required: false },
      { key: 'company', label: 'Empresa', type: 'texto_corto', required: false },
      { key: 'email', label: 'Email', type: 'email', required: true },
      { key: 'phone', label: 'Teléfono', type: 'telefono', required: true },
    ],
    consentText:
      'Acepto la política de privacidad y autorizo que contacten conmigo.',
    questions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  const { error } = await supabase.from('forms').insert(formRow(form))
  if (error) throw new Error(error.message)
  await saveConfig(organizationId, 'form', form.id, formConfig(form))
  await refresh()
  return form
}

/**
 * Guarda el formulario completo: cabecera, configuración y preguntas.
 *
 * Cada operación indica en el mensaje de error qué paso ha fallado, para que
 * el editor pueda mostrar la causa real y permitir reintentar.
 */
export async function saveForm(form: Form): Promise<void> {
  // La fila del formulario puede no existir todavía (formularios creados
  // antes de conectar la base de datos): se inserta o actualiza.
  const { error } = await supabase
    .from('forms')
    .upsert(formRow(form), { onConflict: 'id' })
  if (error) {
    throw new Error(`No se ha podido guardar el formulario: ${error.message}`)
  }

  try {
    await saveConfig(form.organizationId, 'form', form.id, formConfig(form))
  } catch (err) {
    throw new Error(
      `No se ha podido guardar la configuración del formulario: ${
        err instanceof Error ? err.message : 'error desconocido'
      }`,
    )
  }

  // Las preguntas se reemplazan por completo para respetar orden y borrados.
  const { data: existing, error: readError } = await supabase
    .from('form_questions')
    .select('id')
    .eq('form_id', form.id)
  if (readError) {
    throw new Error(`No se han podido leer las preguntas: ${readError.message}`)
  }

  const keep = form.questions.map((q) => q.id)
  const remove = (existing ?? [])
    .map((row: { id: string }) => row.id)
    .filter((id: string) => !keep.includes(id))
  if (remove.length) {
    const { error: deleteError } = await supabase
      .from('form_questions')
      .delete()
      .in('id', remove)
    if (deleteError) {
      throw new Error(
        `No se han podido eliminar las preguntas borradas: ${deleteError.message}`,
      )
    }
  }

  if (form.questions.length) {
    const { error: questionsError } = await supabase
      .from('form_questions')
      .upsert(
        form.questions.map((question, index) =>
          questionRow(
            { ...question, order: index + 1 },
            form.id,
            form.organizationId,
          ),
        ),
        { onConflict: 'id' },
      )
    if (questionsError) {
      throw new Error(
        `No se han podido guardar las preguntas: ${questionsError.message}`,
      )
    }
  }
  await refresh()
}

export async function duplicateForm(formId: string): Promise<Form | undefined> {
  const original = getForm(formId)
  if (!original) return undefined
  const copy: Form = {
    ...(JSON.parse(JSON.stringify(original)) as Form),
    id: newId('form'),
    name: `${original.name} (copia)`,
    slug: `${original.slug}-copia-${Date.now().toString(36)}`,
    status: 'borrador',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  copy.questions = original.questions.map((question) => ({
    ...question,
    id: newId('q'),
    formId: copy.id,
  }))
  const { error } = await supabase.from('forms').insert(formRow(copy))
  if (error) throw new Error(error.message)
  await saveForm(copy)
  return copy
}

export async function setFormStatus(
  formId: string,
  status: Form['status'],
): Promise<void> {
  const form = getForm(formId)
  if (!form) return
  const { error } = await supabase
    .from('forms')
    .update({ is_active: status === 'activo' })
    .eq('id', formId)
  if (error) throw new Error(error.message)
  await saveConfig(form.organizationId, 'form', formId, {
    ...formConfig(form),
    status,
  })
  await refresh()
}

export async function restoreForm(formId: string): Promise<void> {
  const { error } = await supabase
    .from('forms')
    .update({ deleted_at: null })
    .eq('id', formId)
  if (error) throw new Error(error.message)
  await refresh()
}

/** Borrado lógico: el formulario pasa a la papelera de formularios. */
export async function deleteForm(formId: string): Promise<void> {
  const { error } = await supabase
    .from('forms')
    .update({ deleted_at: new Date().toISOString(), is_active: false })
    .eq('id', formId)
  if (error) throw new Error(error.message)
  await supabase.from('landing_pages').update({ form_id: null }).eq('form_id', formId)
  await refresh()
}
